import { Form, Radio } from "antd";
import BaseModalForm from "components/BaseModalForm";
import message from "components/CustomMessage";
import { MODAL_SIZE_MEDIUM } from "constants/Layout";
import { PermissionSetMap } from "constants/RBACConstants";
import _ from "lodash";
import React, { forwardRef, useMemo, useState } from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { hasPermission } from "utils/authService";
import {
  FieldTypes,
  emailValidator,
  nameRegexValidator,
  urlRegexValidator,
} from "utils/formUtils";
import ModalUtil from "utils/modalUtil";
import { duplicateNameValidator } from "utils/validatorUtil";

import AzureBoardMappingForm from "./AzureBoardMappingForm";

type AzureBoardLoginDataTypes = {
  id?: number;
  name: string;
  userName: string;
  apiToken: string;
  organisation: string;
  url?: string;
};

type AzureBoardConfigLoginFormProps = {
  name: string;
  loginConfigList: AzureBoardLoginDataTypes[];
  handleMapping: (response: any) => void;
  config: AzureBoardLoginDataTypes;
  onUpdateConfigComponent: (shouldUpdate?: boolean) => void;
  afterClose: () => void;
  displayName: string;
};

function AzureBoardConfiLoginForm(
  { name, loginConfigList, ...props }: AzureBoardConfigLoginFormProps,
  ref: any,
) {
  const [auth, setAuth] = useState(() =>
    props.config?.url
      ? props.config.url === "https://dev.azure.com"
        ? "cloud"
        : "onPrem"
      : "cloud",
  );
  const isEdit = !_.isUndefined(props.config);
  const [form] = Form.useForm();

  const onOk = async (fields: Record<string, any>) => {
    const data = fields as AzureBoardLoginDataTypes;
    try {
      const payload = data;
      const response = await ToolsIntegrationsAPI[
        !isEdit
          ? "postTicketSystemToolLoginDetails"
          : "updateTicketSystemToolLoginDetails"
      ]("AZURE_BOARD", payload, props.config?.id).promise;

      message.success(`Azure Board Login Configuration created`);
      if (!isEdit) {
        ModalUtil.show({
          content: (
            <AzureBoardMappingForm
              onUpdateConfigComponent={props.onUpdateConfigComponent}
              name={name}
              loginConfigId={response.id}
            />
          ),
        });
      }
      props?.onUpdateConfigComponent(true);
    } catch (err: any) {
      console.log(err);
      if (!_.isEmpty(err.response.data.errors)) {
        const { field, defaultMessage } = err.response.data.errors[0];
        form.setFields([
          {
            name: field,
            errors: [defaultMessage],
          },
        ]);
      }
      return Promise.reject(err.response?.data?.message);
    }
  };

  const authRadio = useMemo(
    () => (
      <Radio.Group
        onChange={(e) => setAuth(e.target.value)}
        value={auth}
        className="m-b-l"
      >
        <Radio value={"cloud"}>Cloud</Radio>
        <Radio value={"onPrem"}>On Prem</Radio>
      </Radio.Group>
    ),
    [auth],
  );

  const modalProps = {
    title: <>{isEdit ? "Edit" : "Add"} Azure Board Login Credentials </>,
    width: MODAL_SIZE_MEDIUM,
    disabled: !hasPermission(
      isEdit ? PermissionSetMap.EDIT_TOOL_CONFIG : PermissionSetMap.WRITE_TOOLS,
    ),
  };

  const fields = [
    {
      type: "Label",
      value: authRadio,
    },
    {
      name: "name",
      label: "Name",
      colSpan: 12,
      props: {
        autoFocus: true,
      },
      required: true,
      rules: [
        duplicateNameValidator({ list: loginConfigList }),
        nameRegexValidator,
      ],
    },
    {
      name: "userName",
      label: auth === "cloud" ? "Email" : "User Name",
      colSpan: 12,
      ...(auth === "cloud" ? { rules: [emailValidator] } : {}),
      required: true,
    },
    {
      name: "apiToken",
      label: "API Token",
      type: FieldTypes.PASSWORD,
      colSpan: 12,
      required: true,
    },
    {
      name: "organisation",
      label: auth === "cloud" ? "Organization" : "Collection",
      colSpan: 12,
      required: true,
    },
    ...(auth === "onPrem"
      ? [
          {
            name: "url",
            label: "URL",
            colSpan: 12,
            rules: [urlRegexValidator],
            required: true,
          },
        ]
      : []),
  ];

  const ModalFormComponent = BaseModalForm;

  return (
    <ModalFormComponent
      modalProps={modalProps}
      ref={ref}
      afterClose={props.afterClose}
      initialValues={{ ...props.config, version: "V3" }}
      form={form}
      fields={fields}
      onOk={onOk}
      title={modalProps.title}
    />
  );
}

export default forwardRef(AzureBoardConfiLoginForm);
